import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/app/AppShell';
import { useDepartments, useCreateDepartment, useUpdateDepartment, useDeleteDepartment, type Department } from '@/lib/departments';
import { useEmployees } from '@/lib/employees';
import { confirm } from '@/components/ui/ConfirmDialog';

const SUGGESTED_DEPARTMENTS = [
  'Engineering', 'Product', 'Design', 'Sales', 'Marketing', 'Customer Success',
  'Finance', 'People & HR', 'Operations', 'Legal', 'IT',
];

export function DepartmentsPage() {
  const { t } = useTranslation();
  const departments = useDepartments();
  const employees = useEmployees({});
  const create = useCreateDepartment();
  const update = useUpdateDepartment();
  const del = useDeleteDepartment();
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const membersOf = (deptId: string) => (employees.data ?? []).filter((e) => e.departmentId === deptId);

  function openCreate() { setEditId(null); setName(''); setParentId(''); setError(null); setCreating(true); }
  function openEdit(d: Department) { setEditId(d.id); setName(d.name); setParentId(d.parentId ?? ''); setError(null); setCreating(true); }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editId) {
        await update.mutateAsync({ id: editId, name: name.trim(), parentId: parentId || undefined });
      } else {
        await create.mutateAsync({ name: name.trim(), parentId: parentId || undefined });
      }
      setName(''); setParentId(''); setCreating(false); setEditId(null);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }
  const saving = create.isPending || update.isPending;

  // Competitor-style quick-start: suggested common departments the admin can
  // one-click add (we never auto-seed — org structure is company-specific).
  const [seeding, setSeeding] = useState(false);
  async function seedCommonDepartments() {
    setSeeding(true);
    const existing = new Set((departments.data ?? []).map((d) => d.name.toLowerCase()));
    try {
      for (const name of SUGGESTED_DEPARTMENTS) {
        if (existing.has(name.toLowerCase())) continue;
        await create.mutateAsync({ name });
      }
    } finally {
      setSeeding(false);
    }
  }

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-4xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('departments.title')}</h1>
          <button onClick={openCreate} className="inline-flex items-center px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition">{t('departments.addDepartment')}</button>
        </div>

        <section className="mt-6 rounded-2xl border border-ink-200 bg-white overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-ink-600">
              <tr><th className="text-start font-semibold px-4 py-2">{t('departments.name')}</th><th className="text-start font-semibold px-4 py-2">{t('departments.parent')}</th><th className="text-start font-semibold px-4 py-2">{t('departments.members', { defaultValue: 'Members' })}</th><th className="text-end font-semibold px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {departments.isLoading && <tr><td colSpan={4} className="text-center text-ink-500 py-6">{t('common.loading')}</td></tr>}
              {departments.data?.length === 0 && (
                <tr><td colSpan={4} className="text-center py-8">
                  <p className="text-sm text-ink-500">{t('departments.empty')}</p>
                  <button
                    onClick={seedCommonDepartments}
                    disabled={seeding}
                    className="mt-3 inline-flex items-center px-4 py-2 rounded-lg border border-ink-300 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50 transition"
                  >
                    {seeding
                      ? t('departments.addingSuggested', { defaultValue: 'Adding…' })
                      : t('departments.addSuggested', { defaultValue: '✨ Add common departments' })}
                  </button>
                  <p className="mt-2 text-[11px] text-ink-400">{t('departments.suggestedHint', { defaultValue: 'Engineering, Product, Sales, Finance, People & HR… — edit or remove any after.' })}</p>
                </td></tr>
              )}
              {(departments.data ?? []).map((d) => {
                const members = membersOf(d.id);
                const isExpanded = expandedId === d.id;
                return (
                <Fragment key={d.id}>
                <tr className="hover:bg-ink-50/40">
                  <td className="px-4 py-2 text-ink-900 font-medium">
                    <button onClick={() => setExpandedId(isExpanded ? null : d.id)} className="hover:text-accent-600 transition">
                      {isExpanded ? '▾' : '▸'} {d.name}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-ink-500 text-xs">{parentName(d.parentId, departments.data ?? []) ?? t('departments.noParent')}</td>
                  <td className="px-4 py-2 text-ink-600 text-xs num">{members.length}</td>
                  <td className="px-4 py-2 text-end whitespace-nowrap">
                    <button
                      onClick={() => openEdit(d)}
                      className="text-accent-700 hover:text-accent-600 text-xs font-semibold me-3"
                    >
                      {t('departments.edit', { defaultValue: 'Edit' })}
                    </button>
                    <button
                      onClick={async () => {
                        const ok = await confirm({
                          title: t('departments.confirmDeleteTitle'),
                          description: t('departments.confirmDeleteBody', { name: d.name }),
                          tone: 'destructive',
                          confirmLabel: t('departments.delete'),
                        });
                        if (ok) del.mutate(d.id);
                      }}
                      className="text-rose-600 hover:text-rose-500 text-xs font-semibold"
                    >
                      {t('departments.delete')}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-ink-50/40">
                    <td colSpan={4} className="px-4 py-2">
                      {members.length === 0 ? (
                        <p className="text-xs text-ink-500">{t('departments.noMembers', { defaultValue: 'No employees assigned. Set their department in People → Edit.' })}</p>
                      ) : (
                        <ul className="flex flex-wrap gap-2">
                          {members.map((m) => (
                            <li key={m.id}>
                              <Link to={`/app/people/${m.id}`} className="inline-flex items-center px-2 py-1 rounded-md border border-ink-200 bg-white text-xs text-ink-700 hover:text-accent-600 hover:border-accent-300 transition">
                                {m.preferredName || m.legalNameLocal}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </section>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => { setCreating(false); setEditId(null); }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-semibold text-ink-900">{editId ? t('departments.editDepartment', { defaultValue: 'Edit department' }) : t('departments.addDepartment')}</h2>
            <label className="block">
              <span className="block text-xs font-medium text-ink-700 mb-1">{t('departments.name')}<span className="text-warn-500 ms-1">*</span></span>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-ink-700 mb-1">{t('departments.parent')}</span>
              <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none">
                <option value="">{t('departments.noParent')}</option>
                {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            {error && <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setCreating(false); setEditId(null); }} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('common.cancel')}</button>
              <button type="submit" disabled={!name.trim() || saving} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">{editId ? t('common.save', { defaultValue: 'Save' }) : t('common.create')}</button>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}

function parentName(parentId: string | null, all: Department[]): string | null {
  if (!parentId) return null;
  return all.find((d) => d.id === parentId)?.name ?? null;
}
